import React, { useEffect } from 'react';

import { TextInput } from '@components/admin/Forms';
import { RadioGroupInput } from '@components/Form';
import { FormStepDivider } from '@components/Forms/FormStepSection';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useBookingInquiryLimits } from '@hooks/queries/useBookingInquiryLimits';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useField } from 'formik';

const strings = createStrings({
  ja: {
    thisFieldIsRequired: 'この欄は必須です',
    pickupDateHint: () => (
      <>
        *<b>集荷日</b>とは？
        <br />
        有料集荷サービスをご利用の場合、お客様ご自身で集荷日を指定することが可能です。 <br />
        ご指定の日までにお荷物をご用意ください。 <br />
        集荷日を起点として最短納品日(概算)を算出いたします。
      </>
    ),
    moveInDateHint: () => (
      <>
        *<b>荷受倉庫への搬入予定日</b>とは？
        <br />
        有料集荷サービスをご利用しない場合、荷受倉庫への搬入予定日までに荷受倉庫へ商品をお送りいただく必要があります。
        <br />
        荷受倉庫への搬入予定日を起点として最短納品日(概算)を算出いたします。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  choicesContainer: {
    ...common.textGray700,
    padding: '1rem',
  },
  hintContainer: {
    marginTop: '0.5rem',
    ...common.smallerText,
  },
}));

export const PickupInfoInput: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [hasPickupsField] = useField<'true' | 'false'>('has_pickups');

  const [pickupDateField, , pickupDateHelpers] = useField<string>('pickup_date');

  const [moveInDateField, , moveInDateHelpers] = useField<string>('move_in_date');

  useEffect(() => {
    if (pickupDateField.value && !moveInDateField.value) {
      moveInDateHelpers.setValue(pickupDateField.value);
    } else if (moveInDateField.value && !pickupDateField.value) {
      pickupDateHelpers.setValue(moveInDateField.value);
    }
  }, [hasPickupsField.value]);

  const bookingInquiryLimits = useBookingInquiryLimits();

  return (
    <>
      <div className={classes.choicesContainer}>
        <div className={clsx(classes.middleText)}>
          {bookingInquiryStrings.hasPickupsPrompt}
          <span className={classes.textError}>*</span>
        </div>

        <div style={{ height: 10 }} />

        <RadioGroupInput
          noLabel
          label=""
          name="has_pickups"
          iconSize="small"
          formControlLabelClasses={{
            label: classes.middleTextImportant,
          }}
          options={[
            {
              value: String(false),
              label: formStrings.notUse,
            },
            {
              value: String(true),
              label: formStrings.use,
            },
          ]}
        />
      </div>
      <FormStepDivider />
      <div className={classes.choicesContainer}>
        {hasPickupsField.value === 'true' && (
          <>
            <div className={clsx(classes.middleText)}>
              {bookingInquiryStrings.pickupDate}
              <span className={classes.textError}>*</span>
            </div>
            <div style={{ height: 10 }} />
            <TextInput
              name="pickup_date"
              noLabel
              placeholder={strings.thisFieldIsRequired}
              type="date"
              min={dayjs().format('YYYY-MM-DD')}
              max={bookingInquiryLimits?.data?.max_etd}
            />

            <div style={{ height: 10 }} />

            <div className={classes.hintContainer}>{strings.pickupDateHint()}</div>
          </>
        )}
        {hasPickupsField.value === 'false' && (
          <>
            <div className={clsx(classes.middleText)}>
              {bookingInquiryStrings.moveInDate}
              <span className={classes.textError}>*</span>
            </div>
            <div style={{ height: 10 }} />
            <TextInput
              name="move_in_date"
              noLabel
              placeholder={strings.thisFieldIsRequired}
              type="date"
              min={dayjs().format('YYYY-MM-DD')}
              max={bookingInquiryLimits?.data?.max_etd}
            />
            <div style={{ height: 10 }} />

            <div className={classes.hintContainer}>{strings.moveInDateHint()}</div>
          </>
        )}
      </div>
    </>
  );
};
